import { Box, Flex, Text } from '@chakra-ui/react'
import {
  bloodPressureField,
  EncounterType,
  FieldMapValue,
  heightField,
  pulseField,
  respiratoryRateField,
  temperatureField,
  VitalSource,
  VitalSourceKey,
  weightField,
} from '@hb/shared'
import { DataView } from '@components/DataView/DataView'
import { VitalSignsSection } from '@hb/shared'
import { useMemo } from 'react'
import { SectionLabel } from '../../SectionLabel'

const pregnancyVitalsField = {
  name: 'Vitals',
  children: {
    weight: weightField,
    temperature: temperatureField,
    bloodPressure: bloodPressureField,
    pulse: pulseField,
  },
}

const patientVitalsField = {
  name: 'Vitals',
  children: {
    height: heightField,
    weight: weightField,
    temperature: temperatureField,
    bloodPressure: bloodPressureField,
    pulse: pulseField,
    respiratoryRate: respiratoryRateField,
  },
}

const getBmi = (weight: number, height: number) => {
  if (!weight || !height) return null
  return ((weight * 703) / (height * height)).toFixed(1)
}

export const ExamVitalsView = ({
  section,
  data,
  examType,
}: {
  section: VitalSignsSection
  data: FieldMapValue
  examType: EncounterType
}) => {
  const vital = data?.vital as Record<string, any> | undefined
  const hasVitals = data?.hasVitals as boolean | undefined
  const vitalSources = data?.vitalSources as
    | Partial<Record<VitalSourceKey, VitalSource>>
    | undefined

  const isPregnancyExam = useMemo(
    () => [EncounterType.ANTEPARTUM, EncounterType.POSTPARTUM].includes(examType),
    [examType],
  )

  const bmi = useMemo(() => {
    if (isPregnancyExam || !vital) return null
    return getBmi(vital.weight, vital.height)
  }, [isPregnancyExam, vital])

  // §H: Build display-layer vital object with pt-reported suffixes applied to text fields.
  const displayVital = useMemo(() => {
    if (!vital) return {}
    if (!isPregnancyExam || !vitalSources) return vital
    const result = { ...vital }
    const textKeys: VitalSourceKey[] = ['temperature', 'weight', 'pulse', 'o2Saturation']
    for (const key of textKeys) {
      if (vitalSources[key] === 'pt-reported' && result[key] != null) {
        result[key] = `${result[key]} *(pt-reported)*`
      }
    }
    return result
  }, [vital, isPregnancyExam, vitalSources])

  if (section.optional && !hasVitals) {
    return (
      <Box w="100%">
        <SectionLabel>Vitals</SectionLabel>
        <Text fontStyle="italic" color="gray.500" fontSize="sm">
          No vitals recorded
        </Text>
      </Box>
    )
  }

  return (
    <Box w="100%">
      <SectionLabel>Vitals</SectionLabel>
      <DataView
        noHeader
        alwaysExpanded
        field={isPregnancyExam ? pregnancyVitalsField : patientVitalsField}
        data={displayVital}
        boxProps={{ border: 'none', borderBottom: 'none' }}
      />
      {isPregnancyExam && vitalSources?.bloodPressure === 'pt-reported' && vital?.bloodPressure ? (
        <Text px={1} mt={0.5} fontSize="xs" color="gray.500" fontStyle="italic">
          BP *(pt-reported)*
        </Text>
      ) : null}
      {!isPregnancyExam && bmi ? (
        <Flex align="center" gap={2} px={1} mt={1}>
          <Text fontSize="sm" fontWeight={600} color="gray.600">
            BMI:
          </Text>
          <Text fontSize="sm">{bmi}</Text>
        </Flex>
      ) : null}
    </Box>
  )
}
